import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal } from 'solid-js'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import api from '~/services/api'

interface DataModel {
  id: string
  name: string
  label: string
  fields: { name: string; type: string; required?: boolean }[]
  createdAt: string
}

const BuilderModelsPage: Component = () => {
  const alert = useAlert()
  const [models, { refetch }] = createResource(async () => {
    try {
      const { data } = await api.get('/data-models')
      return (data?.data?.items ?? []) as DataModel[]
    } catch {
      return []
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<DataModel | null>(null)
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const toast = useToast()

  const openCreate = () => {
    setEditing(null)
    setShowModal(true)
  }
  const openEdit = (m: DataModel) => {
    setEditing(m)
    setShowModal(true)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      const payload = { name: formData.get('name') as string, label: formData.get('label') as string }
      if (editing()) {
        await api.put(`/data-models/${editing()!.id}`, payload)
        toast.addToast({ title: 'Modelo actualizado', type: 'success' })
      } else {
        await api.post('/data-models', payload)
        toast.addToast({ title: 'Modelo creado', type: 'success' })
      }
      setShowModal(false)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar modelo',
      message: '¿Estás seguro de eliminar este modelo de datos?'
    })
    if (!ok) return
    try {
      await api.delete(`/data-models/${id}`)
      toast.addToast({ title: 'Modelo eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Nombre', accessor: (m: DataModel) => m.name },
    { header: 'Etiqueta', accessor: (m: DataModel) => m.label },
    { header: 'Campos', accessor: (m: DataModel) => `${m.fields?.length ?? 0} campos` }
  ]

  return (
    <>
      <Title>Builder - Modelos de Datos | Florez Jaramillo</Title>
      <Table<DataModel>
        title="Modelos de Datos"
        subtitle="Define la estructura de tus datos"
        headerActions={
          <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
            Nuevo Modelo
          </Button>
        }
        data={models() ?? []}
        columns={columns}
        keyField="id"
        loading={models.loading}
        searchable
        searchPlaceholder="Buscar modelo..."
        emptyMessage="Aún no hay modelos de datos"
        actions={m => (
          <ButtonGroup size="sm" variant="soft">
            <Button color="warning" onClick={() => openEdit(m)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
            <Button color="danger" onClick={() => handleDelete(m.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </ButtonGroup>
        )}
      />
      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar Modelo' : 'Nuevo Modelo'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="model-form" onSubmit={handleSubmit} initialValues={editing() ? { name: editing()!.name, label: editing()!.label } : undefined}>
            <div class="space-y-4">
              <Input name="name" label="Nombre interno" required placeholder="ej: producto" />
              <Input name="label" label="Etiqueta" required placeholder="ej: Producto" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting()} form="model-form">
              {isSubmitting() ? 'Guardando...' : editing() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default BuilderModelsPage
